import React, { FC, PropsWithChildren, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateStringOrNull, PaceTaskScreen } from '@symfa-inc/providence-types';
import { Form } from 'antd';
import classNames from 'classnames';
import { HttpService } from '@core/services';
import { ProjectHttpService } from '@core/services/http';
import { useDidUpdateEffect, useSaveChanged } from '@core/utils/hooks';
import { isValidForm } from '@core/utils/methods';
import { FormProps, ProjectPaceTask } from '@models/interfaces';
import {
  getPaceTaskDifference,
  NotificationsLoader,
  paceTaskMapper,
  PaceTaskTable,
} from '@shared/components';
import { PrimaryButton } from '@shared/modules';
import { CommonActions, ProjectActions } from '@store/actions';
import { LeasingEffects } from '@store/effects';
import {
  LeasingSelectors,
  PaceTasksSelectors,
  ProjectCategoriesSelectors,
  UserSelectors,
} from '@store/selectors';
import { LeasingInformation } from '../leasing-information';
import { ProjectInformation } from '../project-information';
import { SiteInformation } from '../site-information';

import './styles.scss';

const { useForm } = Form;

export const LeasingSummary: FC<FormProps> = ({
  isEditing = false,
  toggleEditing,
}: PropsWithChildren<FormProps>) => {
  const dispatch = useDispatch();

  const [paceTaskForm] = useForm();

  const { siteInfo, projectInfo, leasingInfo } = useSelector(
    LeasingSelectors.getLeasingDetailsSummaryData,
  );
  const currentPaceTasks = useSelector(PaceTasksSelectors.getPaceTasks);
  const arePaceTasksChanged = useSelector(
    PaceTasksSelectors.arePaceTasksChanged,
  );
  const projectCategoriesTableVisible = useSelector(
    ProjectCategoriesSelectors.getProjectCategoriesTableVisible,
  );
  const leasingFetching = useSelector(LeasingSelectors.isFetching);
  const PACEFetching = useSelector(PaceTasksSelectors.isFetching);
  const roles = useSelector(UserSelectors.getUserRoles);

  const onSubmit = async (): Promise<void> => {
    const [paceTasksData, keys] = getPaceTaskDifference(
      currentPaceTasks,
      paceTaskForm,
      roles,
    );

    if (await isValidForm(paceTaskForm, keys)) {
      try {
        await HttpService.getHttpRequests(
          ProjectHttpService,
        ).updateProjectPaceTasks(
          projectInfo.projectId,
          PaceTaskScreen.Leasing,
          paceTasksData,
        );

        toggleEditing?.();

        dispatch(
          ProjectActions.updateProjectPaceTaskFieldsFromSummaryPageTable.done(
            paceTasksData,
          ),
        );

        const legalSubmittedId = currentPaceTasks.find(
          (paceTask: ProjectPaceTask) =>
            paceTask.taskName === 'Legal Submitted',
        )?.id;

        const legalAssignedId = currentPaceTasks.find(
          (paceTask: ProjectPaceTask) => paceTask.taskName === 'Legal Assigned',
        )?.id;

        if (legalSubmittedId || legalAssignedId) {
          LeasingEffects.setLeasingWorkflowFields(
            legalSubmittedId &&
              (paceTasksData.find(pace => pace.paceTask === legalSubmittedId)
                ?.actualDate as DateStringOrNull),
            legalAssignedId &&
              (paceTasksData.find(pace => pace.paceTask === legalAssignedId)
                ?.actualDate as DateStringOrNull),
          );
        }

        dispatch(ProjectActions.setArePACETasksChanged.done(false));

        NotificationsLoader.notificationSuccess(
          'PACE tasks have been updated!',
        );
      } catch (e) {
        console.error(e);
      }
    }
  };

  const onCancel = (): void => {
    paceTaskForm.setFieldsValue(paceTaskMapper(currentPaceTasks));

    dispatch(ProjectActions.setArePACETasksChanged.done(false));

    toggleEditing?.();
  };

  const buttonsDisabled =
    leasingFetching || PACEFetching || !arePaceTasksChanged;

  useEffect(
    () => (): void => {
      dispatch(CommonActions.setHasUnsubmittedData.done(false));
    },
    [],
  );

  useDidUpdateEffect(() => {
    dispatch(CommonActions.setHasUnsubmittedData.done(arePaceTasksChanged));
  }, [arePaceTasksChanged]);

  useSaveChanged(isEditing, onSubmit, onCancel);

  return (
    <div className="prov-leasing-summary ">
      <div
        className={classNames('tabs-wrap', {
          'tabs-wrap_with-actions': isEditing,
          'categories-table-open': projectCategoriesTableVisible,
        })}
      >
        <div className="prov-leasing-summary__wrap">
          <div className="info-panel">
            <SiteInformation
              className="info-panel__information"
              data={siteInfo}
            />
            <ProjectInformation
              className="info-panel__information"
              colSpan="24"
              data={projectInfo}
            />
            <LeasingInformation
              className="info-panel__information"
              data={leasingInfo}
            />
          </div>
          <PaceTaskTable
            isEditing={isEditing}
            data={currentPaceTasks || []}
            form={paceTaskForm}
          />
        </div>
      </div>
      {isEditing && (
        <div className="prov-leasing-handle__btns">
          <div className="action-wrap">
            <PrimaryButton
              title="Submit"
              disabled={buttonsDisabled}
              onClick={onSubmit}
            />
            <PrimaryButton
              title="Cancel"
              disabled={buttonsDisabled}
              type="default"
              onClick={onCancel}
            />
          </div>
        </div>
      )}
    </div>
  );
};
